import { Elements } from '@stripe/react-stripe-js';
import React, {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { loadStripe } from '@stripe/stripe-js';

import type {
  BillingContact,
  BillingMode,
  Customer,
  PersistedStripePrice,
  SanitizedStripeSource,
  SanitizedSubscription,
} from '@sine/sine-core-api-types';
import type { Team } from '@sine/sine-team-switcher';
import { type ISelectOption, sortItem, useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../../helpers/parseServerError';
import { allCurrencyOptions } from '../../../services/billing/billing.constants';
import { usePrices } from '../../../services/billing/billing.hooks';
import billingService from '../../../services/billing/billing.service';
import {
  type Currency,
  type HydratedBillableSite,
  LicenseType,
  type PlanSelectOption,
} from '../../../services/billing/billing.types';
import reportingService from '../../../services/reporting/reporting.service';
import { SITE_STATUS } from '../constants/billing';
import { country2currency, currencies } from '../constants/currencies';
import { getExistingPlanOptions, isSelfServeSubscription } from '../helpers/subscriptions';

const stripePromise = loadStripe(window.env.REACT_APP_STRIPE_CLIENT_KEY);

interface BillingContextValues {
  isUserSystemAdminActing: boolean;
  isLoading: boolean;
  currentTeam: Team;
  billingContact: BillingContact | undefined;
  customer: Customer | undefined;
  currencyOptions: Array<ISelectOption | undefined>;
  existingPlanOptions: Array<PlanSelectOption>;
  dateOptions: Array<ISelectOption> | undefined;
  hasPresetCurrency: boolean;
  customerCurrency: Currency;
  cards: Array<SanitizedStripeSource>;
  defaultCard: SanitizedStripeSource | undefined;
  subscriptions: Array<SanitizedSubscription>;
  sites: Array<HydratedBillableSite>;
  licencedSites: Array<HydratedBillableSite>;
  unlicencedSites: Array<HydratedBillableSite>;
  prices: Array<PersistedStripePrice>;
  trials: Array<SanitizedSubscription>;
  billingMode: BillingMode['mode'] | undefined;
  loadCustomer: () => Promise<void>;
  loadSubscriptions: () => Promise<Array<SanitizedSubscription>>;
  loadSites: () => Promise<Array<HydratedBillableSite>>;
  loadPrices: (selectedCurrency?: Currency) => Promise<void>;
  loadBillingContact: () => Promise<void>;
  loadBillingMode: () => Promise<void>;
  loadAllData: () => Promise<void>;
}

const defaultBillingContext: BillingContextValues = {
  isUserSystemAdminActing: false,
  isLoading: false,
  currentTeam: { id: '', name: '' },
  billingContact: undefined,
  customer: undefined,
  currencyOptions: [],
  existingPlanOptions: [],
  dateOptions: [],
  hasPresetCurrency: false,
  customerCurrency: 'aud',
  cards: [],
  defaultCard: undefined,
  subscriptions: [],
  sites: [],
  licencedSites: [],
  unlicencedSites: [],
  prices: [],
  trials: [],
  billingMode: undefined,
  loadCustomer: async () => {},
  loadSubscriptions: async () => [],
  loadSites: async () => [],
  loadPrices: async () => {},
  loadBillingContact: async () => {},
  loadBillingMode: async () => {},
  loadAllData: async () => {},
};

const BillingContext = createContext(defaultBillingContext);

type BillingProviderProps = PropsWithChildren<{
  isUserSystemAdminActing: boolean;
  currentTeam: Team;
}>;

export default function BillingProvider({ isUserSystemAdminActing, currentTeam, children }: BillingProviderProps) {
  const { addNotification } = useNotification();
  const [loadingCounter, setLoadingCounter] = useState(0);
  const [customer, setCustomer] = useState(defaultBillingContext.customer);
  const [currencyOptions, setCurrencyOptions] = useState(defaultBillingContext.currencyOptions);
  const [hasPresetCurrency, setHasPresetCurrency] = useState(defaultBillingContext.hasPresetCurrency);
  const [customerCurrency, setCustomerCurrency] = useState(defaultBillingContext.customerCurrency);
  const [cards, setCards] = useState(defaultBillingContext.cards);
  const [defaultCard, setDefaultCard] = useState(defaultBillingContext.defaultCard);
  const [subscriptions, setSubscriptions] = useState(defaultBillingContext.subscriptions);
  const [sites, setSites] = useState(defaultBillingContext.sites);
  const [licencedSites, setLicencedSites] = useState(defaultBillingContext.licencedSites);
  const [unlicencedSites, setUnlicencedSites] = useState(defaultBillingContext.unlicencedSites);
  const [prices, setPrices] = useState(defaultBillingContext.prices);
  const [billingContact, setBillingContact] = useState(defaultBillingContext.billingContact);
  const [dateOptions, setDateOptions] = useState(defaultBillingContext.dateOptions);
  const [existingPlanOptions, setExistingPlanOptions] = useState(defaultBillingContext.existingPlanOptions);
  const [trials, setTrials] = useState(defaultBillingContext.trials);
  const [billingMode, setBillingMode] = useState(defaultBillingContext.billingMode);
  const { data: priceData } = usePrices(currentTeam.id, customerCurrency);

  const incrementLoadingCounter = () => setLoadingCounter((prev) => prev + 1);
  const decrementLoadingCounter = () => setLoadingCounter((prev) => Math.max(prev - 1, 0));

  const customerCurrencyRef = useRef(customerCurrency);
  useEffect(() => {
    customerCurrencyRef.current = customerCurrency;
  }, [customerCurrency]);

  useEffect(() => {
    const existingCurrency =
      customer?.externalData.currency ||
      // In the case of an outdated customer object from Stripe, or
      // webhook event 'customer.updated' not properly delivered to Sine
      // endpoint, we'll rely on the first existing subscription's plan
      // currency to avoid showing the currency dropdown, which allegedly
      // prevents "multiple currency conflicts errors" from Stripe.
      subscriptions.filter(isSelfServeSubscription)[0]?.externalData?.plan?.currency;

    const firstSiteCountryCode = sites[0]?.countryCode?.toLowerCase();
    const isSupportedCountryCode = firstSiteCountryCode && Object.keys(country2currency).includes(firstSiteCountryCode);
    const _countryCode = isSupportedCountryCode ? firstSiteCountryCode : currencies['usd'].country;

    setHasPresetCurrency(!!existingCurrency);
    setCustomerCurrency((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return existingCurrency || prev || country2currency[_countryCode];
    });
    setCurrencyOptions(allCurrencyOptions.sort((a, b) => sortItem(a.label, b.label)));
  }, [customer, subscriptions, sites]);

  const loadCustomer: BillingContextValues['loadCustomer'] = useCallback(async () => {
    incrementLoadingCounter();
    const _customer = await billingService.getCustomer(currentTeam.id);

    if (_customer) {
      setCustomer(_customer);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setCustomerCurrency((prev) => _customer.externalData.currency || prev || 'aud');
      setCards(_customer.externalData.sources.data);
      setDefaultCard(_customer.externalData.default_source);
    }

    decrementLoadingCounter();
  }, [currentTeam.id]);

  const loadSubscriptions: BillingContextValues['loadSubscriptions'] = useCallback(async () => {
    incrementLoadingCounter();

    const _subscriptions = await billingService.listSubscriptions(currentTeam.id);

    // Split trials and subscriptions into separate arrays
    const trialsSubArray = [];
    const subscriptionsSubArray = [];

    for (const subscription of _subscriptions) {
      if (
        subscription.licences?.find(
          (licence) => licence.type === LicenseType.TRIAL_BASIC || licence.type === LicenseType.TRIAL_SYSADMIN,
        )
      ) {
        trialsSubArray.push(subscription);
      } else {
        subscriptionsSubArray.push(subscription);
      }
    }
    setSubscriptions(subscriptionsSubArray);
    setTrials(trialsSubArray);
    decrementLoadingCounter();

    return _subscriptions;
  }, [currentTeam.id]);

  const loadSites: BillingContextValues['loadSites'] = useCallback(async () => {
    incrementLoadingCounter();
    const [_sites, _siteStats] = await Promise.all([
      billingService.listSites(currentTeam.id),
      reportingService.getSiteStatistics({ teamId: currentTeam.id }),
    ]);

    const hydratedSites: Array<HydratedBillableSite> = _sites.map((site) => ({
      ..._siteStats.find((stats) => stats.id === site.id)!,
      ...site,
    }));

    const _licencedSites = hydratedSites.filter((site) => !!site.licence);
    const _unlicencedSites = hydratedSites.filter(
      (site) => !site.licence && (site.status === SITE_STATUS.ENABLED || site.status === SITE_STATUS.DISABLED),
    );

    setSites(hydratedSites);
    setLicencedSites(_licencedSites);
    setUnlicencedSites(_unlicencedSites);
    decrementLoadingCounter();

    return hydratedSites;
  }, [currentTeam.id]);

  const loadPrices: BillingContextValues['loadPrices'] = useCallback(
    async (selectedCurrency?: Currency) => {
      incrementLoadingCounter();

      const currency = selectedCurrency || customerCurrencyRef.current;

      if (priceData && priceData.length !== 0) setPrices(priceData);
      setCustomerCurrency(currency);
      decrementLoadingCounter();
    },
    [priceData],
  );

  const loadBillingContact: BillingContextValues['loadBillingContact'] = useCallback(async () => {
    try {
      setBillingContact(await billingService.getBillingContact(currentTeam.id));
    } catch (err) {
      addNotification({
        heading: `Unable to retrieve billing contact`,
        variant: 'danger',
      });
    }
  }, [addNotification, currentTeam.id]);

  const loadBillingMode: BillingContextValues['loadBillingMode'] = useCallback(async () => {
    try {
      setBillingMode(await billingService.getBillingMode(currentTeam.id));
    } catch (err) {
      addNotification({
        heading: `Unable to retrieve billing mode`,
        variant: 'danger',
      });
    }
  }, [addNotification, currentTeam.id]);

  const loadAllData: BillingContextValues['loadAllData'] = useCallback(async () => {
    try {
      incrementLoadingCounter();
      await Promise.all([
        loadCustomer(),
        loadSubscriptions(),
        loadSites(),
        loadPrices(),
        loadBillingContact(),
        loadBillingMode(),
      ]);
    } catch (error: any) {
      error.customMsg = 'Failed to load team billing information.';
      addNotification({
        heading: 'An error occurred',
        message: parseServerError(error),
        variant: 'danger',
      });
    } finally {
      decrementLoadingCounter();
    }
  }, [addNotification, loadBillingContact, loadCustomer, loadPrices, loadSites, loadSubscriptions, loadBillingMode]);

  const _getExistingPlanOptions = useCallback(() => {
    if (sites.length) {
      const { allOptions, renewDateOptions } = getExistingPlanOptions(
        subscriptions.filter((sub) => isSelfServeSubscription(sub)),
        sites,
      );

      setExistingPlanOptions(allOptions);
      setDateOptions(renewDateOptions);
    }
  }, [subscriptions, sites]);

  useEffect(() => {
    // TODO: use react-query
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    _getExistingPlanOptions();
  }, [_getExistingPlanOptions]);

  const context = {
    isUserSystemAdminActing,
    isLoading: loadingCounter > 0,
    currentTeam,
    billingContact,
    customer,
    currencyOptions,
    existingPlanOptions,
    dateOptions,
    hasPresetCurrency,
    customerCurrency,
    cards,
    defaultCard,
    subscriptions,
    sites,
    licencedSites,
    unlicencedSites,
    prices,
    trials,
    billingMode,
    loadCustomer,
    loadSubscriptions,
    loadSites,
    loadPrices,
    loadBillingContact,
    loadBillingMode,
    loadAllData,
  };

  return (
    <BillingContext.Provider value={context}>
      <Elements stripe={stripePromise}>{children}</Elements>
    </BillingContext.Provider>
  );
}

export function useBilling() {
  return useContext(BillingContext);
}
